import React from "react";
import "../PlanCreationModal.scss";
import {
  Grid,
  Card,
  Typography,
  IconButton,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { DeleteOutline as DeleteIcon } from "@mui/icons-material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import TextInput from "./TextInput";
import Dropdown from "./Dropdown";
import SelectMonth from "./SelectMonth";
import { checkDateRangeValid } from "../utils";
import SelectYear from "./SelectYear";
import { getAge } from "@instream-solutions/instream-design-system/lib/Utilities/shared";
import { isNumericTextboxValid } from "../../../../common/validation";

const AssetCard = ({
  asset,
  data: initialData = {},
  fullWidth = false,
  canDelete = false,
  onDelete = () => {},
  handleChange,
  clients,
  contributionLimit,
}) => {
  const getRetirementYear = (id) => {
    const client = clients[0];
    const coClient = clients[1];

    return coClient && coClient.id === id
      ? new Date(coClient.dateOfBirth).getFullYear() + coClient.retirementAge
      : new Date(client.dateOfBirth).getFullYear() + client.retirementAge;
  };

  const calculateMaximumPlanLength = (item) => {
    if (item.owner === null) return 30;

    const client = clients[0];
    const coClient = clients[1];

    const clientPlanLength =
      1 + (client.lifeExpectancyAge - getAge(client.dateOfBirth));
    const coClientPlanLength = coClient
      ? 1 + (coClient.lifeExpectancyAge - getAge(coClient.dateOfBirth))
      : 0;

    if (item.owner.id === client.id) return clientPlanLength;
    return coClientPlanLength;
  };

  return (
    <Card className="asset-card">
      {canDelete && (
        <IconButton
          className="delete-asset-btn"
          aria-label="delete"
          onClick={onDelete}
          size="large"
        >
          <DeleteIcon />
        </IconButton>
      )}
      <Grid
        container
        spacing={4}
        justifyContent="space-between"
        className="monthly-expenses-inputs"
      >
        <Grid item md={12}>
          <Typography className="highlighted-text" component="h6">
            {asset.accountType.name}
          </Typography>
        </Grid>
        <Grid item md={12}>
          <Typography className="-mb-4" variant="body1">
            Owner
          </Typography>
        </Grid>

        <Grid item md={fullWidth ? 3 : 12}>
          <Dropdown
            value={asset.owner ? asset.owner.id : null}
            label={""}
            options={clients}
            onChange={(e) => {
              handleChange("ownerId")(e.target.value);
            }}
            isRequired={asset.isEdited}
          />
        </Grid>
        <Grid item md={fullWidth ? 3 : 12}>
          <Typography className="-mb-4" variant="body1">
            Current Value
          </Typography>
        </Grid>
        <Grid item md={fullWidth ? 3 : 12}>
          <TextInput
            className="full-width"
            type="number"
            label=""
            value={asset.currentValue}
            adornment="$"
            onChange={handleChange("currentValue")}
          />
        </Grid>
        {!fullWidth && (
          <Grid item md={12} className="contribution-title-and-type">
            <Typography className="-mb-4" variant="body1">
              Contribution
            </Typography>
            <Select
              value={asset.contributionType}
              onChange={handleChange("contributionType")}
              displayEmpty
              inputProps={{ "aria-label": "Without label" }}
              className="highlighted-text type border-less-input"
            >
              <MenuItem value={"monthly"}>Dollar Amount</MenuItem>
              <MenuItem value={"percent"}>% of Income</MenuItem>
            </Select>
          </Grid>
        )}
        {asset.contributionType === "monthly" && (
          <Grid
            item
            md={fullWidth ? 3 : 12}
            className="monthly-contribution-div"
          >
            <TextInput
              className="full-width"
              type="number"
              value={asset.monthlyContribution}
              adornment={"$"}
              onChange={handleChange("monthlyContribution")}
              className="input"
            />
            <Select
              value={asset.frequencyType}
              onChange={handleChange("frequencyType")}
              displayEmpty
              inputProps={{ "aria-label": "Without label" }}
              className="highlighted-text type border-less-input"
            >
              <MenuItem value={"YEARLY"}>Yearly</MenuItem>
              <MenuItem value={"MONTHLY"}>Monthly</MenuItem>
              <MenuItem value={"WEEKLY"}>Weekly</MenuItem>
            </Select>
            {contributionLimit != null && (
              <div>
                <InfoOutlinedIcon className="contribution-limit-icon" />
                <div className="contribution-limit-tips">
                  Annual contribution limit: {contributionLimit}
                </div>
              </div>
            )}
          </Grid>
        )}
        {asset.contributionType === "percent" && (
          <Grid
            item
            md={fullWidth ? 3 : 12}
            className="percent-contribution-div"
          >
            <TextInput
              className="full-width"
              type="percent"
              value={asset.percentContribution}
              adornment={"%"}
              onChange={handleChange("percentContribution")}
              className="input"
            />
            <Select
              value={asset.frequencyType}
              onChange={handleChange("frequencyType")}
              displayEmpty
              inputProps={{ "aria-label": "Without label" }}
              className="highlighted-text type border-less-input"
            >
              <MenuItem value={"YEARLY"}>Yearly</MenuItem>
              <MenuItem value={"MONTHLY"}>Monthly</MenuItem>
              <MenuItem value={"WEEKLY"}>Weekly</MenuItem>
            </Select>
          </Grid>
        )}

        <Grid item md={6}>
          <Grid item xs={12}>
            <Typography className="-mb-4" variant="body1">
              Start Date
            </Typography>
          </Grid>
        </Grid>

        <Grid item md={6}>
          <Grid item xs={12}>
            <Typography className="-mb-4" variant="body1">
              End Date
            </Typography>
          </Grid>
        </Grid>

        <Grid item md={6}>
          <Grid item container direction="row">
            <Grid item>
              <SelectMonth
                value={parseInt(asset.startMonth)}
                year={asset.startYear}
                owner={asset.owner}
                planLength={calculateMaximumPlanLength(asset)}
                client={clients[0]}
                coClient={clients[1]}
                onChange={handleChange("startMonth")}
                limitLength={true}
                error={
                  !checkDateRangeValid(
                    asset.startYear,
                    asset.startMonth,
                    asset.endYear,
                    asset.endMonth
                  )
                }
              />
              /&nbsp;
              <SelectYear
                value={parseInt(asset.startYear)}
                onChange={handleChange("startYear")}
                length={calculateMaximumPlanLength(asset)}
                error={
                  !checkDateRangeValid(
                    asset.startYear,
                    asset.startMonth,
                    asset.endYear,
                    asset.endMonth
                  )
                }
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item md={6}>
          <Grid item container direction="row">
            <Grid item>
              <SelectMonth
                value={parseInt(asset.endMonth)}
                year={asset.endYear}
                owner={asset.owner}
                planLength={calculateMaximumPlanLength(asset)}
                client={clients[0]}
                coClient={clients[1]}
                onChange={handleChange("endMonth")}
                limitLength={true}
                error={
                  !checkDateRangeValid(
                    asset.startYear,
                    asset.startMonth,
                    asset.endYear,
                    asset.endMonth
                  )
                }
              />
              /&nbsp;
              <SelectYear
                value={parseInt(asset.endYear)}
                onChange={handleChange("endYear")}
                length={calculateMaximumPlanLength(asset)}
                error={
                  !checkDateRangeValid(
                    asset.startYear,
                    asset.startMonth,
                    asset.endYear,
                    asset.endMonth
                  )
                }
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item md={12}>
          <Typography className="-mb-4" variant="body1">
            Expected rate of return
          </Typography>
        </Grid>

        <Grid item md={fullWidth ? 3 : 6}>
          <TextInput
            className="full-width"
            value={asset.preRetirementAllocation}
            adornment="%"
            type="percent"
            onChange={handleChange("preRetirementAllocation")}
            error={
              !isNumericTextboxValid(asset.preRetirementAllocation, 0, 100)
            }
          />
          <Typography variant="caption">Before retirement</Typography>
        </Grid>
        <Grid item md={fullWidth ? 3 : 6}>
          <TextInput
            className="full-width"
            value={asset.postRetirementAllocation}
            adornment="%"
            type="percent"
            onChange={handleChange("postRetirementAllocation")}
            error={
              !isNumericTextboxValid(asset.postRetirementAllocation, 0, 100)
            }
          />
          <Typography variant="caption">After retirement</Typography>
        </Grid>
      </Grid>
    </Card>
  );
};

export default AssetCard;
